import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileDown, FileText, AlertTriangle, Clock, Trophy, Gift, MessageSquare, UserX } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Label } from "../../components/common";
import { Button } from "../../components/ui/button";
import { BarSeries } from "../../components/charts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import ResponsesSummary from "../../components/ResponsesSummary";
import { feedbackHref } from "../../lib/feedbackLink";

const PERIOD_DAYS = { daily: 14, weekly: 56, monthly: 365 };

const PERIODS = [{ k: "daily", l: "Daily" }, { k: "weekly", l: "Weekly" }, { k: "monthly", l: "Monthly" }];

export default function Reports() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("daily");
  const [locId, setLocId] = useState("");
  const [locations, setLocations] = useState([]);
  const [data, setData] = useState(null);
  const [qdata, setQdata] = useState(null);
  const [loading, setLoading] = useState(true);
  const isCorp = user?.role === "corporate_admin";
  const navigate = useNavigate();
  const fb = (extra) => navigate(feedbackHref({ location_id: locId, days: PERIOD_DAYS[period], ...extra }));

  useEffect(() => {
    if (isCorp) api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, [isCorp]);

  useEffect(() => {
    const q = locId ? `?location_id=${locId}` : "";
    api.get(`/reports/questions${q}`).then((r) => setQdata(r.data)).catch(() => setQdata(null));
  }, [locId]);

  useEffect(() => {
    setLoading(true);
    const q = `period=${period}${locId ? `&location_id=${locId}` : ""}`;
    api.get(`/reports/overview?${q}`).then((r) => setData(r.data)).catch(() => toast.error("Failed to load report")).finally(() => setLoading(false));
  }, [period, locId]);

  const reload = () => {
    const q = `period=${period}${locId ? `&location_id=${locId}` : ""}`;
    api.get(`/reports/overview?${q}`).then((r) => setData(r.data)).catch(() => {});
  };
  const ackAlert = async (id) => {
    try { await api.post(`/reports/reward-alerts/${id}/ack`); reload(); } catch { toast.error("Could not dismiss"); }
  };

  const saveBlob = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const download = (fmt) => saveBlob(`/reports/overview.${fmt}?period=${period}${locId ? `&location_id=${locId}` : ""}`, `servantage_report_${period}.${fmt}`);
  const downloadFormer = (fmt) => saveBlob(`/reports/former-staff.${fmt}${locId ? `?location_id=${locId}` : ""}`, `servantage_former_staff.${fmt}`);

  if (loading && !data) return <div className="flex min-h-[60vh] items-center justify-center text-slate-400">Building report…</div>;
  if (!data) return null;

  const k = data.kpis; const r = k.ratings_per_person_per_day;
  const industry = r.industry == null ? "—" : r.industry;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Label>{data.organization?.name}{data.location ? ` · ${data.location.name}` : " · All locations"}</Label>
          <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Reports</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isCorp && (
            <Select value={locId || "all"} onValueChange={(v) => setLocId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-44" data-testid="report-location"><SelectValue placeholder="All locations" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All locations</SelectItem>
                {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="flex rounded-lg border border-slate-200 bg-white p-1" data-testid="period-toggle">
            {PERIODS.map((p) => (
              <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`period-${p.k}`}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${period === p.k ? "bg-[#4338CA] text-white" : "text-slate-500 hover:text-slate-800"}`}>
                {p.l}
              </button>
            ))}
          </div>
          <Button variant="outline" onClick={() => download("csv")} data-testid="export-csv"><FileDown className="mr-1 h-4 w-4" /> CSV</Button>
          <Button variant="outline" onClick={() => download("pdf")} data-testid="export-pdf"><FileText className="mr-1 h-4 w-4" /> PDF</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-former-staff"><UserX className="mr-1 h-4 w-4" /> Export former staff</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => downloadFormer("csv")} data-testid="export-former-csv"><FileDown className="mr-2 h-4 w-4" /> CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadFormer("pdf")} data-testid="export-former-pdf"><FileText className="mr-2 h-4 w-4" /> PDF</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* reward anniversary alerts */}
      {data.reward_alerts?.length > 0 && (
        <div className="space-y-2" data-testid="reward-alerts">
          {data.reward_alerts.map((a) => (
            <div key={a.id} className="flex items-center justify-between rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3" data-testid={`reward-alert-${a.id}`}>
              <div className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-indigo-600" />
                <span className="text-sm font-semibold text-indigo-900">{a.message}</span>
              </div>
              <Button size="sm" variant="outline" onClick={() => ackAlert(a.id)} data-testid={`ack-alert-${a.id}`}>Dismiss</Button>
            </div>
          ))}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Verified ratings" value={k.total_ratings} onClick={() => fb({})} testId="kpi-verified-ratings" />
        <Kpi label="Avg rating" value={k.avg_rating} />
        <Kpi label="Service Index" value={k.ssi} accent />
        <Kpi label="Active staff" value={k.active_employees} />
      </div>

      <ResponsesSummary locationId={locId} days={PERIOD_DAYS[period]} testId="report-responses-summary" />

      {/* A) ratings per person per day */}
      <Section title="Ratings per person per day" icon={Trophy}>
        <div className="grid grid-cols-3 gap-3">
          <Compare label="You" value={r.you} accent />
          <Compare label="Company" value={r.company} />
          <Compare label="Industry" value={industry} />
        </div>
      </Section>

      {/* benchmarks service index */}
      <Section title="Service Index benchmark">
        <BenchBar label="You" value={data.benchmarks.you} accent />
        <BenchBar label="Company average" value={data.benchmarks.company} />
        <BenchBar label="Industry average" value={data.benchmarks.industry} />
      </Section>

      {/* C) trend */}
      <Section title={`Rating trend (${period})`}>
        <BarSeries data={data.trend} yKey="count" xKey="label" testId="trend-chart" />
      </Section>

      {/* B) peak times */}
      <Section title="Peak service times" icon={Clock}>
        {data.peak.insight && <p className="mb-3 rounded-lg bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-800">{data.peak.insight}</p>}
        <BarSeries data={data.peak.hours} yKey="count" xKey="label" color="#0369A1" testId="peak-chart" />
      </Section>

      {/* top locations / vs top 3 */}
      {data.top_locations?.length > 0 && (
        <Section title={data.scope === "store" ? "This location vs top 3" : "Top locations"} icon={Trophy}>
          <div className="space-y-2">
            {data.top_locations.map((l, i) => (
              <Rankline key={l.id} rank={i + 1} name={l.name} ssi={l.ssi} ratings={l.ratings}
                highlight={data.location && l.id === data.location.id} onClick={() => navigate(`/app/store?location=${l.id}`)} />
            ))}
            {data.scope === "store" && data.current_location_rank &&
              !data.top_locations.some((l) => l.id === data.location.id) && (
                <Rankline rank={null} name={`${data.current_location_rank.name} (this location)`}
                  ssi={data.current_location_rank.ssi} ratings={data.current_location_rank.ratings} highlight />
              )}
          </div>
        </Section>
      )}

      {/* exceptions */}
      {data.exceptions?.length > 0 && (
        <Section title="Exceptions" icon={AlertTriangle}>
          <div className="space-y-2">
            {data.exceptions.map((e, i) => (
              <div key={`${e.label}-${i}`} className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${e.severity === "high" ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`} data-testid={`exception-${i}`}>
                <span className="font-semibold text-slate-800">{e.label}</span>
                <span className="text-slate-500">{e.detail}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* D) rewards */}
      <Section title="Rewards & criteria" icon={Gift}>
        <div className="mb-3 flex flex-wrap gap-2 text-sm">
          <Pill label="Pending" value={data.rewards.allocations.pending} />
          <Pill label="Approved" value={data.rewards.allocations.approved} />
          <Pill label="Fulfilled" value={data.rewards.allocations.fulfilled} />
        </div>
        {data.rewards.rules.length === 0 ? (
          <p className="text-sm text-slate-400">No reward rules configured yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                <Th>Rule</Th><Th>Metric</Th><Th>Threshold</Th><Th>Min ratings</Th><Th>Reward</Th><Th>Status</Th>
              </tr></thead>
              <tbody>
                {data.rewards.rules.map((ru, i) => (
                  <tr key={`${ru.name}-${ru.metric}`} className="border-t border-slate-100">
                    <Td bold>{ru.name}</Td><Td>{ru.metric}</Td><Td>{ru.threshold}</Td><Td>{ru.min_interactions}</Td>
                    <Td>{ru.reward}</Td>
                    <Td>{ru.active ? <span className="font-semibold text-emerald-600">Active</span> : <span className="text-slate-400">Off</span>}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Questions Analytics */}
      {qdata && qdata.questions.length > 0 && (
        <Section title="Question Yes/No rates" icon={MessageSquare}>
          <div className="space-y-2">
            {qdata.overall.map((r) => (
              <div key={r.q} data-testid={`qa-overall-${r.q.slice(0, 12)}`} onClick={() => fb({ question: r.q })} className="cursor-pointer rounded-md p-1 hover:bg-slate-50">
                <div className="mb-1 flex justify-between text-sm">
                  <span className="font-bold text-slate-800">{r.q}</span>
                  <span className="font-semibold text-slate-900">{r.yes_pct}% yes <span className="text-slate-400">({r.yes}/{r.total})</span></span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className="h-2.5 rounded-full bg-emerald-500" style={{ width: `${r.yes_pct}%` }} />
                </div>
              </div>
            ))}
          </div>

          {qdata.by_location?.length > 0 && (
            <div className="mt-6 overflow-x-auto">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">By location (% yes)</p>
              <table className="w-full text-sm" data-testid="qa-by-location">
                <thead><tr className="text-left text-xs uppercase text-slate-400"><Th>Location</Th>{qdata.questions.map((q) => <Th key={q}>{q}</Th>)}</tr></thead>
                <tbody>
                  {qdata.by_location.map((l) => (
                    <tr key={l.id} className="border-t border-slate-100"><Td bold><button className="hover:text-indigo-700 hover:underline" onClick={() => navigate(`/app/store?location=${l.id}`)} data-testid={`qa-loc-link-${l.id}`}>{l.name}</button></Td>
                      {qdata.questions.map((q) => { const r = l.rows.find((x) => x.q === q); return <Td key={q}>{r ? <CellLink onClick={() => fb({ question: q, location_id: l.id })}>{r.yes_pct}%</CellLink> : "—"}</Td>; })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {qdata.by_employee?.length > 0 && (
            <div className="mt-6 overflow-x-auto">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">By employee (% yes)</p>
              <table className="w-full text-sm" data-testid="qa-by-employee">
                <thead><tr className="text-left text-xs uppercase text-slate-400"><Th>Employee</Th><Th>Location</Th>{qdata.questions.map((q) => <Th key={q}>{q}</Th>)}</tr></thead>
                <tbody>
                  {qdata.by_employee.map((e) => (
                    <tr key={e.id} className="border-t border-slate-100"><Td bold><button className="hover:text-indigo-700 hover:underline" onClick={() => navigate(`/app/employees/${e.id}`)} data-testid={`qa-emp-link-${e.id}`}>{e.name}</button></Td><Td>{e.location}</Td>
                      {qdata.questions.map((q) => { const r = e.rows.find((x) => x.q === q); return <Td key={q}>{r ? <CellLink onClick={() => fb({ question: q, employee_id: e.id, location_id: "" })}>{r.yes_pct}%</CellLink> : "—"}</Td>; })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      )}

      {/* employee drilldown */}
      <Section title="Employee drill-down">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="employee-drilldown">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-400">
              <Th>Employee</Th><Th>Location</Th><Th>Ratings</Th><Th>/ day</Th><Th>Index</Th><Th>Primary gap</Th>
            </tr></thead>
            <tbody>
              {data.employees.map((e) => (
                <tr key={e.id} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50" data-testid={`emp-row-${e.id}`} onClick={() => navigate(`/app/employees/${e.id}`)}>
                  <Td bold>{e.name}</Td><Td>{e.location}</Td><Td><CellLink onClick={(ev) => { ev.stopPropagation(); fb({ employee_id: e.id, location_id: "" }); }}>{e.ratings}</CellLink></Td><Td>{e.ratings_per_day}</Td>
                  <Td><span className="font-semibold text-indigo-700">{e.ssi}</span></Td><Td>{e.gap || "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="mb-4 flex items-center gap-2 font-head text-lg font-bold text-slate-900">
        {Icon && <Icon className="h-5 w-5 text-indigo-500" />} {title}
      </h2>
      {children}
    </div>
  );
}
function Kpi({ label, value, accent, onClick, testId }) {
  return (
    <div onClick={onClick} data-testid={testId} className={`rounded-xl border p-4 ${onClick ? "cursor-pointer hover:border-indigo-300" : ""} ${accent ? "border-indigo-200 bg-indigo-50" : "border-slate-200 bg-white"}`}>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 font-head text-2xl font-bold ${accent ? "text-indigo-700" : "text-slate-900"}`}>{value}</p>
    </div>
  );
}
function Compare({ label, value, accent }) {
  return (
    <div className={`rounded-lg border p-3 text-center ${accent ? "border-indigo-200 bg-indigo-50" : "border-slate-200"}`}>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 font-head text-xl font-bold ${accent ? "text-indigo-700" : "text-slate-800"}`}>{value}</p>
    </div>
  );
}
function BenchBar({ label, value, accent }) {
  const pct = Math.min(100, ((value || 0) / 5) * 100);
  return (
    <div className="mb-3">
      <div className="mb-1 flex justify-between text-sm"><span className="text-slate-600">{label}</span><span className="font-semibold text-slate-900">{value == null ? "—" : value}</span></div>
      <div className="h-2.5 w-full rounded-full bg-slate-100"><div className={`h-2.5 rounded-full ${accent ? "bg-[#4338CA]" : "bg-slate-400"}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}
function Rankline({ rank, name, ssi, ratings, highlight, onClick }) {
  return (
    <div onClick={onClick} className={`flex items-center justify-between rounded-lg border px-3 py-2 ${onClick ? "cursor-pointer hover:border-indigo-300" : ""} ${highlight ? "border-indigo-300 bg-indigo-50" : "border-slate-200"}`}>
      <span className="flex items-center gap-2 font-semibold text-slate-800">
        {rank && <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-xs text-white">{rank}</span>}
        {name}
      </span>
      <span className="text-sm text-slate-500">Index <b className="text-indigo-700">{ssi}</b> · {ratings} ratings</span>
    </div>
  );
}
function Pill({ label, value }) {
  return <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-600">{label}: <b className="text-slate-900">{value}</b></span>;
}
const Th = ({ children }) => <th className="px-2 py-2">{children}</th>;
const CellLink = ({ children, onClick }) => <button onClick={onClick} className="font-semibold text-indigo-700 hover:underline">{children}</button>;
const Td = ({ children, bold }) => <td className={`px-2 py-2 ${bold ? "font-semibold text-slate-800" : "text-slate-600"}`}>{children}</td>;
